/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { ClosedEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  IdentityDirectory,
  IdentityDirectory$inboundSchema,
} from "./identitydirectory.js";

/**
 * What sort of subject this is: 'user' for a directory user, 'apikey' for a subject acting under an API key, 'agent' for an agent identity, or 'unattributed' for activity that cannot be attributed to exactly one directory user — an identifier matching no directory row, or one several members claim.
 */
export const IdentityModelKind = {
  User: "user",
  Apikey: "apikey",
  Agent: "agent",
  Unattributed: "unattributed",
} as const;
/**
 * What sort of subject this is: 'user' for a directory user, 'apikey' for a subject acting under an API key, 'agent' for an agent identity, or 'unattributed' for activity that cannot be attributed to exactly one directory user — an identifier matching no directory row, or one several members claim.
 */
export type IdentityModelKind = ClosedEnum<typeof IdentityModelKind>;

/**
 * One subject with every identifier its activity is keyed under, so a client can query each subsystem with the identifier that subsystem expects.
 */
export type IdentityModel = {
  /**
   * The identity URN clients should navigate to. Resolving any URN for the same subject returns this one, so links built from different identifiers converge on a single page.
   */
  canonicalUrn: string;
  /**
   * WorkOS Directory Sync attributes for an identity. Every field is mapped by the customer's IdP: scalar attributes are absent when unset or blank, and 'groups' is always present, empty when the identity belongs to none.
   */
  directory: IdentityDirectory;
  /**
   * The subject's name, falling back to its primary email.
   */
  displayName: string;
  /**
   * Every address the subject is known by — directory email first, then linked AI account emails. Telemetry and cost aggregate over this set.
   */
  emails: Array<string>;
  /**
   * The identifiers agents report for this subject. Risk findings key on these.
   */
  externalUserIds: Array<string>;
  /**
   * What sort of subject this is: 'user' for a directory user, 'apikey' for a subject acting under an API key, 'agent' for an agent identity, or 'unattributed' for activity that cannot be attributed to exactly one directory user — an identifier matching no directory row, or one several members claim.
   */
  kind: IdentityModelKind;
  /**
   * The subject's avatar, when the directory supplied one.
   */
  photoUrl?: string | undefined;
  /**
   * The Speakeasy user ids this identity resolves to, the first being the directory owner. Empty when the subject matches no directory row. Audit logs, chats, user sessions and plugin assignments key on these.
   */
  userIds: Array<string>;
  /**
   * The WorkOS user id, which RBAC role assignments key on. Absent for subjects with no WorkOS link.
   */
  workosUserId?: string | undefined;
};

/** @internal */
export const IdentityModelKind$inboundSchema: z.ZodMiniEnum<
  typeof IdentityModelKind
> = z.enum(IdentityModelKind);

/** @internal */
export const IdentityModel$inboundSchema: z.ZodMiniType<
  IdentityModel,
  unknown
> = z.pipe(
  z.object({
    canonical_urn: z.string(),
    directory: IdentityDirectory$inboundSchema,
    display_name: z.string(),
    emails: z.array(z.string()),
    external_user_ids: z.array(z.string()),
    kind: IdentityModelKind$inboundSchema,
    photo_url: z.optional(z.string()),
    user_ids: z.array(z.string()),
    workos_user_id: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "canonical_urn": "canonicalUrn",
      "display_name": "displayName",
      "external_user_ids": "externalUserIds",
      "photo_url": "photoUrl",
      "user_ids": "userIds",
      "workos_user_id": "workosUserId",
    });
  }),
);

export function identityModelFromJSON(
  jsonString: string,
): SafeParseResult<IdentityModel, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => IdentityModel$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'IdentityModel' from JSON`,
  );
}
